import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type {
  AccountDto,
  CompanySettings,
  CatalogItemDto,
  CreatePurchaseBillRequest,
  PurchaseBillDto,
  PurchaseBillSummary,
  SupplierDto,
} from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { api, ProblemError, UnauthorizedError } from './api';
import BillAdjustments from './BillAdjustments';
import { formatMoney, parseAmount } from './money';
import Loading from './Loading';
import EmptyState from './EmptyState';
import { useRevealOnOpen } from './reveal';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  accounts: readonly AccountDto[];
  canRecord: boolean;
  /** إلغاء فاتورة مورد بقيد عكسى — `journal.manual` لا `journal.post`. */
  canCancel: boolean;
  /** المخزون مفعّل: سطر الفاتورة قد يكون صنفًا مخزنيًا يُدين حساب مخزونه. */
  inventoryEnabled: boolean;
  onSessionLost: () => void;
}

/** اختيار «صنف مخزنى» فى خانة الحساب يُحمل هكذا ويُرسل `itemId` بدل الحساب. */
const ITEM_PREFIX = 'item:';

interface DraftLine {
  id: number;
  description: string;
  quantity: string;
  unitPrice: string;
  accountCode: string;
}

let nextLineId = 1;
const emptyLine = (): DraftLine => ({
  id: nextLineId++,
  description: '',
  quantity: '1',
  unitPrice: '',
  accountCode: '',
});

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

const ISSUE_LABELS: Record<string, string> = {
  BILL_LINE_ACCOUNT: 'الحساب يجب أن يكون مصروفًا أو تكلفة أو أصلاً غير رقابى',
  SUPPLIER_REFERENCE_DUPLICATE: 'فاتورة المورد بهذا الرقم مسجلة من قبل',
  SUPPLIER_NOT_FOUND: 'المورد غير موجود',
  INVOICE_LINE_QUANTITY: 'الكمية يجب أن تكون موجبة',
  INVOICE_ZERO_TOTAL: 'إجمالى الفاتورة يجب أن يكون موجبًا',
  DUE_BEFORE_ISSUE: 'تاريخ الاستحقاق قبل تاريخ الفاتورة',
  TAX_RULE_NOT_APPROVED: 'القاعدة الضريبية غير معتمدة',
  PERIOD_CLOSED: 'الفترة المالية مقفلة',
  INVENTORY_DISABLED: 'المخزون غير مفعّل فى الإعدادات',
  STOCK_ITEM_UNKNOWN: 'الصنف ليس صنفًا مخزنيًا',
  STOCK_BACKDATED: 'تاريخ الفاتورة قبل آخر حركة لهذا الصنف',
};

/** صافى السطر للعرض فقط — الخادم يعيد التسعير بنفس القاعدة وهو المرجع. */
function previewNet(line: DraftLine): Money | null {
  const quantity = parseAmount(line.quantity);
  const price = parseAmount(line.unitPrice);
  if (quantity === null || price === null) return null;
  return Money.of(quantity).mul(price).round(2, 'half_up');
}

/**
 * فواتير الموردين: تسجيل بسطور على حسابات مصروف، وقائمة بالمسدد والمتبقى. الفاتورة
 * تُرحَّل لحظة تسجيلها، ورقم فاتورة المورد لا يُسجَّل مرتين لنفس المورد.
 */
export default function PurchaseBills({
  companyId,
  currency,
  accounts,
  canRecord,
  canCancel,
  inventoryEnabled,
  onSessionLost,
}: Props) {
  const [suppliers, setSuppliers] = useState<SupplierDto[]>([]);
  const [bills, setBills] = useState<PurchaseBillSummary[] | null>(null);
  const [supplierId, setSupplierId] = useState('');
  const [billDate, setBillDate] = useState(today);
  const [supplierReference, setSupplierReference] = useState('');
  const [lines, setLines] = useState<DraftLine[]>(() => [emptyLine()]);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState<{ code: string; message: string; lineIndex?: number }[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<PurchaseBillDto | null>(null);
  const [selected, setSelected] = useState<PurchaseBillDto | null>(null);
  const detailRef = useRevealOnOpen<HTMLDivElement>(selected?.id);
  const [purchaseSettings, setPurchaseSettings] = useState<CompanySettings['purchases'] | null>(
    null,
  );
  const [stockItems, setStockItems] = useState<CatalogItemDto[]>([]);

  // نفس قاعدة الخادم: مصروف أو تكلفة أو أصل ليس حسابًا رقابيًا
  const billable = useMemo(
    () =>
      accounts.filter(
        (account) =>
          account.isPostable &&
          (account.accountClass === 'expense' ||
            account.accountClass === 'cogs' ||
            (account.accountClass === 'asset' && account.subledger === null)),
      ),
    [accounts],
  );

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        setIssues(caught.problem.issues ?? []);
        setFailure(
          caught.problem.issues?.length ? null : (caught.problem.detail ?? caught.problem.title),
        );
        return;
      }
      setFailure(caught instanceof Error ? caught.message : String(caught));
    },
    [onSessionLost],
  );

  const load = useCallback(() => {
    Promise.all([
      api.suppliers(companyId),
      api.purchaseBills(companyId),
      api.companySettings(companyId),
    ])
      .then(([supplierList, billList, settings]) => {
        setSuppliers(supplierList.filter((supplier) => supplier.isActive));
        setBills(billList);
        setPurchaseSettings(settings.settings.purchases);
      })
      .catch(handleError);
  }, [companyId, handleError]);

  useEffect(load, [load]);

  function open(billId: string): void {
    api.purchaseBill(companyId, billId).then(setSelected).catch(handleError);
  }
  useEffect(() => {
    if (!inventoryEnabled) return;
    api
      .catalogItems(companyId, true)
      .then((items) => setStockItems(items.filter((item) => item.kind === 'stock')))
      .catch(handleError);
  }, [companyId, inventoryEnabled, handleError]);

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setIssues([]);
    setFailure(null);
    setRecorded(null);
  }

  function updateLine(id: number, patch: Partial<DraftLine>): void {
    setLines((current) => current.map((line) => (line.id === id ? { ...line, ...patch } : line)));
    touched();
  }

  const nets = lines.map(previewNet);
  const total = nets.every((net) => net !== null) ? Money.sum(nets as Money[]) : null;
  const ready =
    supplierId !== '' &&
    lines.every(
      (line) =>
        line.description.trim() !== '' && line.accountCode !== '' && previewNet(line) !== null,
    ) &&
    total !== null &&
    total.isPositive();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setIssues([]);
    setFailure(null);
    const body: CreatePurchaseBillRequest = {
      supplierId,
      billDate,
      ...(supplierReference.trim() ? { supplierReference: supplierReference.trim() } : {}),
      lines: lines.map((line) => ({
        description: line.description.trim(),
        quantity: parseAmount(line.quantity) ?? line.quantity,
        unitPrice: parseAmount(line.unitPrice) ?? line.unitPrice,
        ...(line.accountCode.startsWith(ITEM_PREFIX)
          ? { itemId: line.accountCode.slice(ITEM_PREFIX.length) }
          : { accountCode: line.accountCode }),
      })),
    };
    try {
      const bill = await api.createPurchaseBill(companyId, idempotencyKey, body);
      setRecorded(bill);
      setLines([emptyLine()]);
      setSupplierReference('');
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      handleError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const generalIssues = issues.filter((issue) => issue.lineIndex === undefined);

  return (
    <>
      {canRecord && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>فاتورة مورد</h2>
          <div className="form-row">
            <label className="grow">
              المورد
              <select
                value={supplierId}
                onChange={(event) => {
                  setSupplierId(event.target.value);
                  touched();
                }}
              >
                <option value="">— اختر موردًا —</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.code} — {supplier.nameAr}
                  </option>
                ))}
              </select>
            </label>
            <label>
              تاريخ الفاتورة
              <input
                type="date"
                value={billDate}
                onChange={(event) => {
                  setBillDate(event.target.value);
                  touched();
                }}
              />
              <Hint>التاريخ المطبوع على فاتورة المورد، لا تاريخ إدخالها.</Hint>
            </label>
            <label>
              رقم فاتورة المورد
              <input
                value={supplierReference}
                maxLength={64}
                onChange={(event) => {
                  setSupplierReference(event.target.value);
                  touched();
                }}
              />
              <Hint>الرقم المطبوع على فاتورة المورد — يمنع تسجيل نفس الفاتورة مرتين.</Hint>
            </label>
          </div>
          {suppliers.length === 0 && (
            <p className="sub">لا موردين بعد — أضف موردًا من شاشة الموردين أولاً.</p>
          )}

          <div className="table-scroll">
            <table className="lines-form">
              <thead>
                <tr>
                  <th>الوصف</th>
                  <th>الكمية</th>
                  <th>السعر</th>
                  <th>الحساب</th>
                  <th>الصافى</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const lineIssues = issues.filter((issue) => issue.lineIndex === index);
                  const net = nets[index];
                  return (
                    <tr key={line.id}>
                      <td data-label="الوصف">
                        <input
                          value={line.description}
                          maxLength={500}
                          onChange={(event) =>
                            updateLine(line.id, { description: event.target.value })
                          }
                        />
                        {lineIssues.map((issue, i) => (
                          <p key={i} className="error">
                            {ISSUE_LABELS[issue.code] ?? issue.message}
                          </p>
                        ))}
                      </td>
                      <td data-label="الكمية">
                        <input
                          inputMode="decimal"
                          value={line.quantity}
                          onChange={(event) =>
                            updateLine(line.id, { quantity: event.target.value })
                          }
                        />
                      </td>
                      <td data-label="السعر">
                        <input
                          inputMode="decimal"
                          value={line.unitPrice}
                          onChange={(event) =>
                            updateLine(line.id, { unitPrice: event.target.value })
                          }
                        />
                      </td>
                      <td data-label="الحساب">
                        <select
                          value={line.accountCode}
                          onChange={(event) => {
                            const value = event.target.value;
                            const item = stockItems.find(
                              (entry) => ITEM_PREFIX + entry.id === value,
                            );
                            updateLine(line.id, {
                              accountCode: value,
                              ...(item && line.description.trim() === ''
                                ? { description: item.nameAr }
                                : {}),
                            });
                          }}
                        >
                          <option value="">— اختر حسابًا —</option>
                          {stockItems.length > 0 && (
                            <optgroup label="أصناف مخزنية (تُدين حساب المخزون)">
                              {stockItems.map((item) => (
                                <option key={item.id} value={ITEM_PREFIX + item.id}>
                                  {item.nameAr} — {item.inventoryAccountCode}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="حسابات">
                            {billable.map((account) => (
                              <option key={account.code} value={account.code}>
                                {account.code} — {account.nameAr}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </td>
                      <td data-label="الصافى" className="num-cell">
                        {net ? formatMoney(net) : '—'}
                      </td>
                      <td>
                        {lines.length > 1 && (
                          <button
                            type="button"
                            className="link"
                            onClick={() => {
                              setLines((current) => current.filter((item) => item.id !== line.id));
                              touched();
                            }}
                          >
                            حذف
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="closing">
                  <td colSpan={4}>الإجمالى (بلا ضريبة مدخلات حتى تُعتمد قواعدها)</td>
                  <td className="num-cell total">{total ? formatMoney(total) : '—'}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {generalIssues.map((issue, i) => (
            <p key={i} className="error">
              {ISSUE_LABELS[issue.code] ?? issue.message}
            </p>
          ))}
          {failure && <p className="error">{failure}</p>}
          {recorded && (
            <p className="sub">
              سُجلت الفاتورة {recorded.number} بإجمالى {formatMoney(recorded.total)} {currency} —
              قيدها {recorded.journalNumber}، والاستحقاق {recorded.dueDate}.
            </p>
          )}
          <div className="form-actions sticky">
            <button type="submit" className="primary" disabled={submitting || !ready}>
              {submitting ? 'جارى التسجيل…' : 'تسجيل الفاتورة'}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setLines((current) => [...current, emptyLine()]);
                touched();
              }}
            >
              سطر جديد
            </button>
            <Hint block>
              تُضاف الفاتورة لما عليك للمورد، وما اشتريته لمخزونك أو مصروفاتك. بعد الترحيل لا
              يُعدَّل ولا يُحذف — الخطأ يُصحَّح بمستند عكسى، حتى يبقى تاريخ كل مبلغ واضحًا.
            </Hint>
          </div>
        </form>
      )}

      <div className="panel">
        <h2>فواتير الموردين</h2>
        {bills === null && !failure && <Loading />}
        {bills?.length === 0 && (
          <EmptyState icon="bill" title="لا فواتير موردين بعد">
            كل فاتورة مورد تسجّلها تُرحَّل إلى دفاترك وتظهر هنا بالمتبقى منها.
          </EmptyState>
        )}
        {bills && bills.length > 0 && (
          <div className="table-scroll">
            <table className="stack-mobile">
              <thead>
                <tr>
                  <th>الرقم</th>
                  <th>رقم المورد</th>
                  <th>المورد</th>
                  <th>التاريخ</th>
                  <th>الاستحقاق</th>
                  <th>الإجمالى</th>
                  <th>المتبقى</th>
                  <th>القيد</th>
                </tr>
              </thead>
              <tbody>
                {bills.map((bill) => (
                  <tr
                    key={bill.id}
                    className="clickable"
                    tabIndex={0}
                    onClick={() => open(bill.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') open(bill.id);
                    }}
                  >
                    <td data-label="الرقم" className="mono">
                      {bill.number}
                    </td>
                    <td data-label="رقم المورد" className="mono">
                      {bill.supplierReference ?? ''}
                    </td>
                    <td data-label="المورد">{bill.supplierName}</td>
                    <td data-label="التاريخ">{bill.billDate}</td>
                    <td data-label="الاستحقاق">{bill.dueDate}</td>
                    <td data-label="الإجمالى" className="num-cell">
                      {formatMoney(bill.total)}
                    </td>
                    <td data-label="المتبقى" className="num-cell">
                      {bill.status === 'cancelled' ? (
                        <span className="pill bad">ملغاة</span>
                      ) : (
                        formatMoney(bill.openBalance)
                      )}
                    </td>
                    <td data-label="القيد" className="mono">
                      {bill.journalNumber}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="panel" ref={detailRef}>
          <h2>
            فاتورة مورد {selected.number} — {selected.supplierName}
            {selected.status === 'cancelled' && <span className="pill bad">ملغاة</span>}
          </h2>
          <p className="sub">
            التاريخ {selected.billDate} • الاستحقاق {selected.dueDate}
            {selected.supplierReference ? ' • رقم المورد ' + selected.supplierReference : ''} •
            القيد {selected.journalNumber}
            {selected.memo ? ' • ' + selected.memo : ''}
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>الوصف</th>
                  <th>الكمية</th>
                  <th>السعر</th>
                  <th>الحساب</th>
                  <th>الصافى</th>
                  <th>الضريبة</th>
                </tr>
              </thead>
              <tbody>
                {selected.lines.map((line) => (
                  <tr key={line.lineNumber}>
                    <td>{line.lineNumber}</td>
                    <td>{line.description}</td>
                    <td className="num-cell">{Money.of(line.quantity).normalize().toString()}</td>
                    <td className="num-cell">{formatMoney(line.unitPrice)}</td>
                    <td className="mono">{line.accountCode}</td>
                    <td className="num-cell">{formatMoney(line.net)}</td>
                    <td className="num-cell">{formatMoney(line.taxAmount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="closing">
                  <td colSpan={6}>الإجمالى ({selected.currency})</td>
                  <td className="num-cell total">{formatMoney(selected.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <BillAdjustments
            key={selected.id}
            companyId={companyId}
            currency={currency}
            bill={selected}
            settings={purchaseSettings}
            canIssueNotes={canRecord}
            canCancel={canCancel}
            onChanged={(bill) => {
              setSelected(bill);
              load();
            }}
            onSessionLost={onSessionLost}
          />
        </div>
      )}
    </>
  );
}
